import { createContext, createElement, useContext, useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useMemberPortalApi } from '../api/memberPortalApiClient'
import { createRacquetSyncStore } from './racquetSyncStore'
import { getMemberMobileAuthBridge } from './memberMobileAuthBridge'
export { projectRacquetMatch } from './racquetSyncStore'
const RacquetSyncContext = createContext(null)

/** Authenticated shell owns sync across result, moment, history and main routes. */
export function RacquetSyncProvider({ clubCode, children }) {
  const { user } = useAuth0()
  const { memberPortalApiGet: get, memberPortalApiPost: post } = useMemberPortalApi()
  const [store, setStore] = useState(null)
  useEffect(() => {
    if (!user?.sub) { setStore(null); return }
    const owner = createRacquetSyncStore({ clubCode, subject: user.sub, get, post })
    setStore(owner)
    owner.restore()
    const sync = () => { if (navigator.onLine) { owner.sync(); owner.revalidate() } }
    const visible = () => { if (!document.hidden) sync() }
    window.addEventListener('online', sync)
    document.addEventListener('visibilitychange', visible)
    const interval = setInterval(() => { if (navigator.onLine && !document.hidden) owner.sync() }, 15000)
    let native, alive = true
    getMemberMobileAuthBridge().addAppStateChangeListener(({ isActive }) => { if (isActive) sync() })
      .then(h => { if (alive) native = h; else h?.remove?.() }).catch(() => {})
    return () => {
      alive = false; owner.dispose(); clearInterval(interval)
      window.removeEventListener('online', sync)
      document.removeEventListener('visibilitychange', visible)
      native?.remove?.()
    }
  }, [clubCode, user?.sub, get, post])
  return createElement(RacquetSyncContext.Provider, { value: store }, children)
}

export function useRacquetMatch(clubCode, matchId) {
  const store = useContext(RacquetSyncContext)
  const [, render] = useState(0), [error, setError] = useState(false), [online, setOnline] = useState(navigator.onLine)
  useEffect(() => {
    if (!store) return
    setError(false)
    const unsubscribe = store.subscribe(() => render(n => n + 1), matchId)
    let alive = true
    store.open(matchId).catch(() => { if (alive) setError(true) })
    return () => { alive = false; unsubscribe() }
  }, [store, matchId])
  useEffect(() => {
    const update = () => setOnline(navigator.onLine)
    window.addEventListener('online', update); window.addEventListener('offline', update)
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update) }
  }, [])
  const methods = useMemo(() => ({
    retryEvent: event => store.submit(matchId, event),
    submit: (operation, voice = {}) => store.submit(matchId, { id: crypto.randomUUID(),
      expectedRevision: store.session(matchId)?.model.revision, operation, provenance: voice.voiceToken ? 'voice' : 'manual', ...voice }),
    sync: () => store?.retry(matchId), discard: () => store.discard(matchId)
  }), [store, matchId])
  const session = store?.session(matchId)
  return { ...methods, model: session?.model, snapshot: session?.snapshot,
    error: error || session?.error, online, saving: session?.saving || false, queue: session?.queue }
}
